<template>
	<Loader v-if="loading" />
	<div v-else class="content">
		<div class="flex">
			<div class="flex-grow text-right">
				<a target="_blank" v-if="auth_url" :href="auth_url"><button class="small-margin-right">
					Auth. screen
					<span class="info">Open the customer-facing screen to authorize the payment</span>
				</button></a>
				<button :disabled="saving" class="small-margin-right" v-on:click="action('resend', true);">
					Re-send email
					<span class="info">Send the email to the customer again, with link to authorization. This will only happen if the status is requires_action.</span>
				</button>
				<button :disabled="saving" class="small-margin-right" v-on:click="action('refresh', false);">
					Refresh
					<span class="info">Pull the latest information about the payment intents</span>
				</button>
				<button v-if="!isCompleted" :disabled="saving" class="small-margin-right" v-on:click="action('update-from-order', true);">
					Update amount
					<span class="info">Synchronize the Woocommerce amount to the payment intent</span>
				</button>
				<button v-if="!isCompleted" :disabled="saving" class="small-margin-right" v-on:click="action('confirm', true);">
					Confirm
					<span class="info">Confirm the intent. This means it will be charged with the selected payment method and amount</span>
				</button>
				<button v-if="!isCompleted" :disabled="saving" class="small-margin-right" v-on:click="action('capture', true);">
					Capture
					<span class="info">Attempt to charge the payment intent</span>
				</button>
				<button v-if="!isCompleted" :disabled="saving" class="small-margin-right" v-on:click="action('cancel', true);">Cancel</button>
			</div>
		</div>

		<div class="padding-top flex full-width">

			<div class="flex-grow flex-basis">
				<h2>Details</h2>
				<div class="input-row small-margin-bottom">
					<div class="label">ID</div>
					<div>{{ intent.id }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Last checked</div>
					<div>{{ intent.last_checked }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Mail sent</div>
					<div>{{ intent.authorization_mail_at }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Status</div>
					<div>
						{{ intent.status }}
						(<a target="_blank" href="https://stripe.com/docs/payments/intents#intent-statuses">View all</a>)
					</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Captured at</div>
					<div>{{ intent.captured_at }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Expired/cancelled at</div>
					<div>{{ intent.expired_at }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Order no.</div>
					<div><a :href="'/#/order/' + order.ID">{{ order.ID }}</a></div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Order status</div>
					<div>{{ order.post_status }}</div>
				</div>
				<div class="input-row small-margin-bottom">
					<div class="label">Customer ID</div>
					<div><a :href="'/#/customer/' + order.customer_id">{{ order.customer_id }}</a></div>
				</div>

				<div class="margin-top">
					<h2>Raw data</h2>
					<textarea v-model="raw_response" readonly="readonly" cols="70" rows="30"></textarea>
				</div>
			</div>
			<div class="flex-grow flex-basis">
				<h2>Payment methods</h2>
				<div
					v-for="method in payment_methods"
					class="border-bottom padding-top padding-bottom">
						<div class="flex">
							{{ method }}
							{{ method.masked_card }}
							<div class="flex-grow text-right">
								<button
									class="smaller"
									:disabled="saving || stripe_response.payment_method == method.options.gateway_id"
									v-on:click="changePaymentMethod(method.options.gateway_id)">Charge this card</button>
							</div>
						</div>
						<div class="note">
							<strong><em>Expires {{ method.expiration_date }}</em></strong>
							&nbsp; | &nbsp;
							{{ method.identifier }}
						</div>
				</div>

				<div class="margin-top">
					<EventViewer :resource="eventViewerResource" />
				</div>
			</div>

		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import EventViewer from '@/components/EventViewer.vue'
export default {
  data: () => ({
    saving: false,
    intent: {},
    order: {},
    payment_methods: [],
    raw_response: '',
    stripe_response: {},
    auth_url: ''
  }),
  components: {
    EventViewer
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    loading () {
      return !this.intent.id || !this.order.ID
    },
    isCompleted () {
      return this.intent.status == 'succeeded'
    },
    eventViewerResource () {
      return 'order:' + this.intent.order_id
    }
  },
  methods: {
    action (method, require_confirmation) {
      if (require_confirmation) {
        if (!confirm('Are you sure?')) {
          return
        }
      }

      Vue.api.post('/payment-intent/' + this.id + '/' + method, {}, {
        before: () => this.saving = true,
        always: () => this.saving = false,
        callback: data => this.loadPaymentIntent()
      })
    },
    reset () {
      this.intent = {}
      this.order = {}
      this.payment_methods = {}
    },
    loadPaymentIntent () {
      this.reset()

      Vue.api.get('/payment-intent/' + this.id + '/advanced', {}, {
        callback: data => {
          this.intent = data.intent
          this.raw_response = data.raw
          this.stripe_response = JSON.parse(data.raw)
          this.auth_url = data.auth_url

          this.loadOrder(data.intent.order_id)
        }
      })
    },
    loadOrder (id) {
      Vue.api.get('/order/' + id, {}, {
        callback: data => {
          this.order = data.order
          this.loadPaymentMethods(data.order.customer_id)
        }
      })
    },
    loadPaymentMethods (customerId) {
      Vue.api.get('/customer/' + customerId + '/payment-methods', {}, {
        callback: data => this.payment_methods = data
      })
    },
    changePaymentMethod (id) {
      Vue.api.put('/payment-intent/' + this.id + '/payment-method', {
        id: id
      }, {
        before: () => this.saving = true,
        always: () => this.saving = false,
        callback: data => this.loadPaymentIntent()
      })
    }
  },
  created () {
    this.loadPaymentIntent()
  }
}
</script>
